import { computed, observable } from 'mobx';
import type { Moment } from '@this/src/lib/moment';
import moment from 'moment';
import _ from 'lodash';
import { walkMinutesText } from '@this/src/util';
import type { HotelStayPlanResponseArgs } from './hotel_stay_plan2';
import HotelStayPlan, { convertHotelStayPlanResponseToArgs } from './hotel_stay_plan2';
import type Organization from '../organization/organization3';
import type ReservingTripItem from '../trip/reserving_trip_item2';
import type { HotelPriceDetailResponseArgs } from './hotel_price_detail';
import HotelPriceDetail, { convertHotelPriceDetailResponseToArgs } from './hotel_price_detail';
import type MarginType from '../organization/margin_type3';

export type HotelBedType = {
  description: string;
  id?: number;
};

export type HotelResponseArgs = {
  id: string;
  search_query_item_id?: number | null;
  package_base_price: number | null;
  price: number;
  name: string | null;
  plan_name: string | null;
  selected_stay_plan_id: string | null;
  stay_plans?: HotelStayPlanResponseArgs[] | null;
  checkin_date?: string;
  checkout_date?: string;
  checkin?: string;
  checkout?: string;
  average_price_with_tax: number | null;
  roomnum: string | null;
  package_type: string | null;
  mandatory_tax: number | null;
  breakfast?: boolean | null;
  detail_path?: string | null;
  room_type?: string | null;
  bed_types?: HotelBedType[] | null;
  refundable: boolean | null;
  average_price: number | null;
  price_detail: HotelPriceDetailResponseArgs | null;
  image: string | null;
  distance: number | null;
  latitude: number | null;
  longitude: number | null;
  station_distance: number | null;
  type: string | null;
  sold_out: boolean | null;
  refund: string | null;
};

export type HotelArgs = {
  id?: string;
  searchQueryItemId?: number | null;
  packageBasePrice?: number | null;
  price?: number;
  name?: string | null;
  planName?: string | null;
  selectedStayPlanId?: string | null;
  stayPlans?: HotelStayPlan[];
  checkinDate?: Moment;
  checkoutDate?: Moment;
  checkin?: string;
  checkout?: string;
  parentItem?: ReservingTripItem;
  averagePriceWithTax?: number | null;
  roomnum?: number;
  packageType?: string | null;
  mandatoryTax?: number | null;
  breakfast?: boolean | null;
  detailPath?: string | null;
  roomType?: string | null;
  bedTypes?: HotelBedType[] | null;
  refundable?: boolean | null;
  averagePrice?: number | null;
  priceDetail?: HotelPriceDetail | null;
  image?: string | null;
  distance?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  stationDistance?: number | null;
  type?: string | null;
  soldOut?: boolean | null;
  refund?: string | null;
};

export const convertHotelResponseToArgs = (response: HotelResponseArgs) => {
  const args: HotelArgs = {
    id: response.id,
    searchQueryItemId: response.search_query_item_id,
    packageBasePrice: response.package_base_price,
    price: response.price,
    name: response.name,
    planName: response.plan_name,
    selectedStayPlanId: response.selected_stay_plan_id,
    stayPlans: response.stay_plans?.map(raw => new HotelStayPlan(convertHotelStayPlanResponseToArgs(raw))) || [],
    checkinDate: response.checkin_date ? moment(response.checkin_date) : undefined,
    checkoutDate: response.checkout_date ? moment(response.checkout_date) : undefined,
    checkin: response.checkin,
    checkout: response.checkout,
    averagePriceWithTax: response.average_price_with_tax,
    roomnum: response.roomnum ? parseInt(response.roomnum, 10) : undefined,
    packageType: response.package_type,
    mandatoryTax: response.mandatory_tax,
    breakfast: response.breakfast,
    detailPath: response.detail_path,
    roomType: response.room_type,
    bedTypes: response.bed_types,
    refundable: response.refundable,
    averagePrice: response.average_price,
    priceDetail: response.price_detail
      ? new HotelPriceDetail(convertHotelPriceDetailResponseToArgs(response.price_detail))
      : null,
    image: response.image,
    distance: response.distance,
    latitude: response.latitude,
    longitude: response.longitude,
    stationDistance: response.station_distance,
    type: response.type,
    soldOut: response.sold_out,
    refund: response.refund
  };
  return args;
};

export default class Hotel {
  @observable id?: string;
  @observable searchQueryItemId?: number | null;
  @observable packageBasePrice?: number | null;
  @observable price: number;
  @observable name?: string | null;
  @observable planName?: string | null;
  @observable selectedStayPlanId?: string | null;
  @observable stayPlans?: HotelStayPlan[];
  @observable checkinDate?: Moment;
  @observable checkoutDate?: Moment;
  @observable checkin?: string;
  @observable checkout?: string;
  @observable parentItem?: ReservingTripItem;
  @observable averagePriceWithTax: number;
  @observable roomnum: number;
  @observable packageType: string | null;
  @observable mandatoryTax?: number | null;
  @observable breakfast: boolean | null;
  @observable detailPath?: string | null;
  @observable roomType?: string | null;
  @observable bedTypes?: HotelBedType[] | null;
  @observable refundable: boolean;
  @observable averagePrice: number;
  @observable priceDetail?: HotelPriceDetail | null;
  @observable image?: string | null;
  @observable distance?: number | null;
  @observable latitude?: number | null;
  @observable longitude?: number | null;
  @observable stationDistance?: number | null;
  @observable type?: string | null;
  @observable soldOut?: boolean | null;
  @observable refund?: string | null;

  constructor(args: HotelArgs = {}) {
    this.id = args.id;
    this.searchQueryItemId = args.searchQueryItemId ?? null;
    this.packageBasePrice = args.packageBasePrice;
    this.price = args.price ?? 0;
    this.name = args.name;
    this.planName = args.planName;
    this.selectedStayPlanId = args.selectedStayPlanId;
    this.stayPlans = args.stayPlans;
    this.checkinDate = args.checkinDate;
    this.checkoutDate = args.checkoutDate;
    this.checkin = args.checkin;
    this.checkout = args.checkout;
    this.parentItem = args.parentItem;
    this.averagePriceWithTax = args.averagePriceWithTax ?? 0;
    this.roomnum = args.roomnum ?? 1;
    this.packageType = args.packageType ?? null;
    this.mandatoryTax = args.mandatoryTax ?? null;
    this.breakfast = args.breakfast ?? null;
    this.detailPath = args.detailPath ?? null;
    this.roomType = args.roomType ?? null;
    this.bedTypes = args.bedTypes ?? null;
    this.refundable = args.refundable ?? false;
    this.averagePrice = args.averagePrice ?? 0;
    this.priceDetail = args.priceDetail;
    this.image = args.image;
    this.distance = args.distance;
    this.latitude = args.latitude;
    this.longitude = args.longitude;
    this.stationDistance = args.stationDistance;
    this.type = args.type;
    this.soldOut = args.soldOut;
    this.refund = args.refund;
  }

  @computed get organization(): Organization | undefined {
    return this.parentItem?.organization;
  }

  @computed get totalPrice(): number {
    return this.price;
  }

  @computed get selectedPlanRestricted(): boolean {
    if (!this.selectedStayPlanId) {
      return false;
    }

    const stayPlan = this.stayPlans?.find(plan => plan.id === this.selectedStayPlanId);
    if (!stayPlan) {
      return false;
    }

    return !!stayPlan.restricted;
  }

  @computed get checkinDateStr(): string | undefined {
    return this.checkinDate && this.checkinDate.format('YYYY/MM/DD');
  }

  @computed get checkoutDateStr(): string | undefined {
    return this.checkoutDate && this.checkoutDate.format('YYYY/MM/DD');
  }

  @computed get isForeign(): boolean {
    return this.parentItem?.foreignExist ?? false;
  }

  @computed get totalBufferAmount(): number {
    if (!this.organization || !this.parentItem) {
      return 0;
    }

    const bufferAmount = this.isForeign
      ? this.organization.getForeignHotelBufferAmount(
          this.parentItem.isHotelPriceExceeded,
          this.parentItem.hotelPriceLimit,
          this.price
        )
      : this.organization.getDomesticHotelBufferAmount(
          this.parentItem.isHotelPriceExceeded,
          this.parentItem.hotelPriceLimit,
          this.price
        );
    return bufferAmount * this.roomnum;
  }

  @computed get bufferAmountText(): string {
    if (this.isForeign) {
      return `${utils.digits(
        this.organization?.getForeignHotelBufferAmount(
          this.parentItem?.isHotelPriceExceeded ?? false,
          this.parentItem?.hotelPriceLimit ?? 0,
          this.price
        ) ?? 0
      )}円 × ${this.roomnum}部屋`;
    }
    return `${utils.digits(
      this.organization?.getDomesticHotelBufferAmount(
        this.parentItem?.isHotelPriceExceeded ?? false,
        this.parentItem?.hotelPriceLimit ?? 0,
        this.price
      ) ?? 0
    )}円 × ${this.roomnum}部屋`;
  }

  @computed get isPackage(): boolean {
    return !_.isNil(this.packageType);
  }

  @computed get stayDays(): number | undefined {
    if (!this.checkinDate || !this.checkoutDate) return undefined;

    return this.checkoutDate.diff(this.checkinDate, 'days');
  }

  @computed get days(): number | undefined {
    return this.stayDays ? this.stayDays + 1 : undefined;
  }

  @computed get checkinText(): string {
    return this.checkin || '-';
  }

  @computed get checkoutText(): string {
    return this.checkout || '-';
  }

  @computed get displayBedType(): string {
    if (!this.bedTypes || this.bedTypes.length === 0) {
      return '-';
    }
    return this.bedTypes[0]?.description || '-';
  }

  @computed get hasMultipleBedTypes(): boolean {
    return this.bedTypes ? this.bedTypes.length > 1 : false;
  }

  @computed get bedTypeNoticeMessage(): string | null {
    if (!this.hasMultipleBedTypes) {
      return null;
    }

    const bedTypesText = (this.bedTypes ?? []).map(bt => bt.description).join('、または');
    return `※本プランでは予約確定後でもベッドタイプは確約されません。${bedTypesText}になる可能性があります。`;
  }

  @computed get averagePriceText(): string | null {
    if (this.averagePrice) {
      return utils.formatPrice(this.averagePrice);
    }
    return null;
  }

  @computed get averagePriceDetailArray(): { title: string; price: string }[] {
    const arr = [];
    if (this.averagePriceText) {
      arr.push({ title: '宿泊料(1泊1室):', price: this.averagePriceText });
    }
    if (this.priceDetail && this.priceDetail.taxAndServiceFee && this.priceDetail.taxAndServiceFee > 0) {
      arr.push({
        title: '税・サービス料:',
        price: utils.formatPrice((this.averagePriceWithTax || 0) - (this.averagePrice || 0))
      });
    }
    return arr;
  }

  @computed get peopleNum(): number {
    return this.parentItem?.peopleNum ?? 1;
  }

  @computed get nightlyPriceText(): string {
    return utils.formatPrice(Number(this.priceDetail && this.priceDetail.nightlyRate));
  }

  @computed get taxText(): string {
    return utils.formatPrice(this.priceDetail && this.priceDetail.taxAndServiceFee);
  }

  @computed get showFee(): boolean {
    return this.parentItem?.showFee ?? true;
  }

  @computed get marginType(): MarginType | undefined {
    return this.parentItem?.marginType;
  }

  @computed get marginAmount(): number {
    if (this.marginType) {
      return this.marginType.calcMarginAmount(this.price || 0, this.roomnum);
    }
    return 0;
  }

  @computed get priceDetailHash(): { [key: string]: string } {
    const h: { [key: string]: string } = {};
    h[`└ 宿泊料(${this.stayDays}泊${this.roomnum}室)`] = this.nightlyPriceText;
    if (this.priceDetail && this.priceDetail.taxAndServiceFee && this.priceDetail.taxAndServiceFee > 0) {
      h['└ 税・サービス料'] = this.taxText;
    }
    const marginAmount = this.marginAmount;
    if (this.showFee && this.marginType && marginAmount > 0) {
      h['└ 手配手数料'] = this.marginType.describe(this.price || 0, this.roomnum);
    }
    return h;
  }

  @computed get priceDetailText(): string {
    const t = `ホテル：${utils.formatPrice(this.totalPrice)}\n`;
    const detail = _.map(this.priceDetailHash, (v, k) => `${k}: ${v}`).join('\n');
    return t + detail;
  }

  @computed get walkminutes(): string | undefined {
    if (this.distance) {
      return walkMinutesText(this.distance);
    }
    return undefined;
  }
}
